import { useEffect, useState } from "react";
import axios from "axios";
import {
  fetchBusinesses,
  deleteBusiness,
  updateBusiness,
  updateVerification
} from "../../services/adminService";
import Pagination from "../../components/Pagination";
import { CATEGORIES } from "../../constants/categories";
import { getCategories } from "../../services/categoryService";
import { getCities } from "../../services/cityService";
import "../../styles/admin.css";

const API = process.env.REACT_APP_API_URL;
const adminHeaders = () => ({ headers: { "x-admin-token": localStorage.getItem("adminToken") || "" } });

function subStatus(b) {
  const sub = b.subscription || {};
  const now = new Date();
  if (sub.paidUntil   && new Date(sub.paidUntil)   > now) return "active";
  if (sub.trialEndsAt && new Date(sub.trialEndsAt)  > now) return "trial";
  if (!sub.trialEndsAt && !sub.paidUntil)                  return "trial";
  return "expired";
}

const SUB_LABEL = { trial: "Trial", active: "Active", expired: "Expired" };
const SUB_CLS   = { trial: "sub-pill sub-pill--trial", active: "sub-pill sub-pill--active", expired: "sub-pill sub-pill--expired" };

function Businesses() {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState(CATEGORIES);
  const [cityList,     setCityList]     = useState([]);

  useEffect(() => {
    getCategories()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const names = data.filter((c) => c.active).map((c) => c.name);
          if (names.length > 0) setCategoryList(names);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getCities()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setCityList(data);
      })
      .catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await fetchBusinesses(page);
      setList(res.data.data || []);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error("Error loading businesses", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteBusiness(deleteId);
    setDeleteId(null);
    loadData();
  };

  const handleUpdate = async () => {
    // Frontend validation — mirrors server-side rules
    if (!editing.businessName?.trim()) {
      setEditError("Business name is required");
      return;
    }
    if (!/^[0-9]{10}$/.test(editing.phone || "")) {
      setEditError("Phone must be a 10-digit number");
      return;
    }
    if (editing.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editing.email)) {
      setEditError("Invalid email format");
      return;
    }

    try {
      await updateBusiness(editing._id, editing);
      setEditing(null);
      setEditError("");
      loadData();
    } catch (err) {
      setEditError(err?.response?.data?.msg || "Error updating business");
    }
  };

  // Toggle verification: Verified ↔ Pending (no confirmation modal needed — reversible action)
  const handleVerify = async (id, newStatus) => {
    await updateVerification(id, newStatus);
    loadData();
  };

  const handleExtendSubscription = async (id) => {
    try {
      await axios.put(`${API}/admin/businesses/${id}/subscription/extend`, {}, adminHeaders());
      loadData();
    } catch (err) {
      alert(err?.response?.data?.msg || "Failed to extend subscription");
    }
  };

  return (
    <div>
      <h2>Businesses</h2>

      {/* LOADING */}
      {loading && (
        <div className="loading-message">
          <span className="loading-spinner" />
          Loading...
        </div>
      )}

      {/* EMPTY STATE */}
      {!loading && list.length === 0 && (
        <div className="empty-state">
          <h3>No businesses registered yet</h3>
          <p>Businesses will appear here once added.</p>
        </div>
      )}

      {/* GRID */}
      <div className="admin-grid">
        {list.map((b) => (
          <div key={b._id} className="admin-card">

            <h3 className="business-title">
              {b.name || b.businessName || "No Name"}
            </h3>

            <p><b>Category:</b> {b.category}</p>
            <p><b>City:</b> {b.city}</p>
            <p><b>Phone:</b> {b.phone}</p>
            <p>
              <b>Verification:</b>{" "}
              <span className={b.verified ? "status-verified" : "status-pending"}>
                {b.verificationStatus || "Pending"}
              </span>
            </p>

            <p>
              <b>Subscription:</b>{" "}
              <span className={SUB_CLS[subStatus(b)]}>
                {SUB_LABEL[subStatus(b)]}
              </span>
            </p>

            <div className="admin-actions">
              <button className="edit" onClick={() => setEditing(b)}>
                Edit
              </button>

              {b.verified ? (
                <button className="unverify" onClick={() => handleVerify(b._id, "Pending")}>
                  Unverify
                </button>
              ) : (
                <button className="verify" onClick={() => handleVerify(b._id, "Verified")}>
                  Verify
                </button>
              )}

              <button
                className={`sub-extend sub-extend--${subStatus(b)}`}
                onClick={() => handleExtendSubscription(b._id)}
                title="Extend subscription by 1 year"
              >
                Extend
              </button>

              <button className="delete" onClick={() => setDeleteId(b._id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="admin-pagination-wrapper">
        <Pagination
          currentPage={page}
          totalPages={pages}
          onPageChange={(p) => setPage(p)}
        />
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Edit Business</h3>

            <input
              type="text"
              placeholder="Business Name"
              value={editing.name || editing.businessName || ""}
              onChange={(e) =>
                setEditing({
                  ...editing,
                  name: e.target.value,
                  businessName: e.target.value
                })
              }
              className="modal-input"
            />

            <input
              type="text"
              placeholder="Owner Name"
              value={editing.ownerName || ""}
              onChange={(e) =>
                setEditing({ ...editing, ownerName: e.target.value })
              }
              className="modal-input"
            />

            <select
              value={editing.category || ""}
              onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              className="modal-input modal-select"
            >
              <option value="" disabled>Select category</option>
              {!categoryList.includes(editing.category) && editing.category && (
                <option value={editing.category} disabled>{editing.category} — not in current list</option>
              )}
              {categoryList.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={editing.city || ""}
              onChange={(e) => setEditing({ ...editing, city: e.target.value })}
              className="modal-input modal-select"
            >
              <option value="" disabled>Select district</option>
              {!cityList.some((c) => c.name === editing.city) && editing.city && (
                <option value={editing.city} disabled>{editing.city} — please select district</option>
              )}
              {Array.from(new Set(cityList.map((c) => c.state))).map((state) => (
                <optgroup key={state} label={state}>
                  {cityList.filter((c) => c.state === state).map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <input
              type="text"
              placeholder="Phone"
              value={editing.phone || ""}
              onChange={(e) =>
                setEditing({ ...editing, phone: e.target.value })
              }
              className="modal-input"
            />

            <input
              type="email"
              placeholder="Email"
              value={editing.email || ""}
              onChange={(e) =>
                setEditing({ ...editing, email: e.target.value })
              }
              className="modal-input"
            />

            <input
              type="text"
              placeholder="Website"
              value={editing.website || ""}
              onChange={(e) =>
                setEditing({ ...editing, website: e.target.value })
              }
              className="modal-input"
            />

            <input
              type="text"
              placeholder="Address"
              value={editing.address || ""}
              onChange={(e) =>
                setEditing({ ...editing, address: e.target.value })
              }
              className="modal-input"
            />

            <textarea
              placeholder="Description"
              value={editing.description || ""}
              onChange={(e) =>
                setEditing({ ...editing, description: e.target.value })
              }
              className="modal-input"
              style={{ minHeight: "80px" }}
            />

            {editError && <p className="modal-error">{editError}</p>}

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => { setEditing(null); setEditError(""); }}>
                Cancel
              </button>
              <button className="btn save" onClick={handleUpdate}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-box small">
            <h3>Delete Business?</h3>
            <p>This action cannot be undone.</p>

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button className="btn delete" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Businesses;
